import { useQueryState } from 'nuqs';
import { useEffect } from 'react';

import { useForecastPaginatedQuery } from 'src/features/forecastPaginated/hooks/useForecastPaginatedQuery';
import { useForecastSession } from 'src/features/forecastSessions/useForecastSession';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

export function useForecastPaginated(): {
  page: number;
  setPage: (page: number) => void;
  data: ForecastDataPoint[];
  queryData?: ForecastDataPoint[];
  sku?: string | null;
  validatedAt?: string | null;
  validatedNumber: number;
  totalProducts?: number;
  hasPrevious: boolean;
  hasNext: boolean;
  sessionId?: string;
  sessionName?: string;
  isLoading: boolean;
  error: Error | null;
} {
  const { activeSession } = useForecastSession();
  const [page, setPage] = useQueryState('page', {
    defaultValue: 1,
    parse: (value) => parseInt(value, 10) || 1,
    serialize: (value) => value.toString(),
  });
  const {
    data,
    queryData,
    sku,
    validatedAt,
    validatedNumber,
    totalProducts,
    hasPrevious,
    hasNext,
    isLoading,
    error,
    sessionId,
    sessionName,
  } = useForecastPaginatedQuery(activeSession?.id ?? '', page);

  useEffect(() => {
    if (sku === null && !isLoading && page !== 1) {
      void setPage(1);
    }
  }, [sku, isLoading, page, setPage]);

  return {
    page,
    setPage,
    data,
    queryData,
    sku,
    validatedAt,
    validatedNumber,
    totalProducts,
    hasPrevious: hasPrevious ?? false,
    hasNext: hasNext ?? false,
    sessionId,
    sessionName,
    isLoading,
    error,
  };
}
